import React, { useContext, useReducer, useEffect, useCallback, useMemo } from 'react';
import useViewConfig from 'util/hooks/useViewConfig';
import useStatus from 'util/hooks/useStatus';
import { refreshContext } from 'components/generics/form/refreshContext';
import GenericEdit from '../genericEdit';
import ContentCreate from '@material-ui/icons/Create';
import GenericShow from '../genericShow';
import { getCustomViewName } from '../utils/viewConfigUtils';
import Toolbar from '../form/Toolbar.aor';
import CloseButton from 'fieldFactory/popovers/PopoverCloseButton';
import SaveButton from '../button/SaveButton';
import { ShowActions, EditActions } from '../viewActions/ActionsWithOverrides';
import { getAccessLevelForEntity } from '../utils/viewConfigUtils/index';
import { Dialog, Button } from '@material-ui/core';
import InlineDeleteButton from '../button/InlineDeleteButton';
import ImageEye from '@material-ui/icons/RemoveRedEye';
import DeleteIcon from '@material-ui/icons/Delete';
import DialogTransitionProps from 'util/DialogTransitionProps';

interface ActionProps {
    resource: string;
    viewName: string;
    basePath: string;
    data?: { id: string };
}

interface ShowActionProps extends ActionProps {
    hasEdit?: boolean;
    showActionChildren?: React.ReactElement<{}>;
    editViewName: string | -1;
    handleEditOpen: () => void;
}
/*
    We create an intermediate component here because the Show view will copy props onto it.
*/
const EntityInspectShowActions: React.FunctionComponent<ShowActionProps> = props => {
    const viewConfig = useViewConfig();
    const { hasEdit = false, resource, viewName, basePath } = props;
    return (
        <ShowActions
            accessLevel={getAccessLevelForEntity(viewConfig, resource)}
            viewName={viewName}
            basePath={basePath}
            data={props.data}
            resource={props.resource}
            {...props}
            hasDefaultEdit={false} // we will override this with our own
        >
            {hasEdit && props.editViewName !== -1 ? (
                <Button data-minAccessLevel={3} color="primary" onClick={props.handleEditOpen}>
                    Edit&nbsp;
                    <ContentCreate />
                </Button>
            ) : null}
            {props.showActionChildren && React.cloneElement(props.showActionChildren, props)}
        </ShowActions>
    );
};

export interface EntityInspectProps {
    showActionChildren?: React.ReactElement<{}>;
    openTo?: 'edit' | 'show';
    onMergeOccurred?: (args: { oldId: string; newId: string }) => void;
    reference: string;
    formId?: string;
    editViewName?: string | -1;
    showViewName?: string;
    renderComponent: (renderProps: {
        formId: string;
        reference: string;
        keyForReload: number;
        selectId: (id: string | number | null) => void;
        onRowSelect: <T extends { id: string }>(selected: T[]) => void;
        selectedId: number | string | null;
    }) => JSX.Element;
    config?: string;
}

type ViewOpenState =
    | {
          type: 'CLOSED';
          keyForReload: number;
      }
    | {
          type: 'OPEN';
          viewType: 'EDIT' | 'SHOW';
          id: string;
          keyForReload: number;
      };
type ViewOpenActions =
    | {
          type: 'SWITCH_TO';
          payload: {
              viewType: 'EDIT' | 'SHOW';
              id?: string;
          };
      }
    | {
          type: 'OPEN';
          payload: {
              id: string;
              viewType: 'EDIT' | 'SHOW';
          };
      }
    | {
          type: 'CLOSE';
      }
    | {
          type: 'MOVED_TO_NEW_ID';
          payload: {
              newId: string;
          };
      }
    | {
          type: 'CLOSE_WITH_SAVE';
      };
const viewOpenReducer = (state: ViewOpenState, action: ViewOpenActions): ViewOpenState => {
    switch (action.type) {
        case 'CLOSE':
            return {
                ...state,
                type: 'CLOSED',
            };
        case 'CLOSE_WITH_SAVE':
            return {
                ...state,
                keyForReload: state.keyForReload + 1,
                type: 'CLOSED',
            };
        case 'SWITCH_TO': {
            if (state.type === 'OPEN') {
                return {
                    ...state,
                    type: 'OPEN',
                    viewType: action.payload.viewType,
                    id: action.payload.id || state.id,
                };
            } else {
                return state;
            }
        }
        case 'OPEN':
            return {
                ...state,
                type: 'OPEN',
                viewType: action.payload.viewType,
                id: action.payload.id,
            };
        case 'MOVED_TO_NEW_ID':
            if (state.type === 'OPEN') {
                return {
                    ...state,
                    id: action.payload.newId,
                };
            }
            return state;
    }
};
const EntityInspect = (props: EntityInspectProps) => {
    const { onMergeOccurred, renderComponent, reference, formId } = props;
    const viewConfig = useViewConfig();
    const refresh = useContext(refreshContext);
    const [viewOpen, dispatchViewOpen] = useReducer(viewOpenReducer, { type: 'CLOSED', keyForReload: 1 });
    const status = useStatus(props.reference, viewOpen.type === 'OPEN' ? viewOpen.id : undefined);
    const wasMovedToId = status && status._type === 'moved' ? status.newId : null;
    const selectedId = viewOpen.type === 'OPEN' ? viewOpen.id : undefined;
    useEffect(() => {
        if (wasMovedToId) {
            if (onMergeOccurred) {
                onMergeOccurred({
                    newId: wasMovedToId,
                    oldId: selectedId,
                });
            }
            dispatchViewOpen({
                type: 'MOVED_TO_NEW_ID',
                payload: { newId: wasMovedToId },
            });
            refresh();
        }
    }, [wasMovedToId, selectedId, onMergeOccurred, dispatchViewOpen, refresh]);
    const onRowSelect = useCallback(
        (selectedRecords: { id: string }[]) => {
            console.log('onRowSelect', selectedRecords);
            dispatchViewOpen({
                type: 'OPEN',
                payload: {
                    viewType: props.openTo === 'edit' ? 'EDIT' : 'SHOW',
                    id: selectedRecords[0] && selectedRecords[0].id,
                },
            });
        },
        [dispatchViewOpen, props.openTo],
    );
    const selectId = useCallback(
        (id: string) => {
            console.log('selectId', id);
            dispatchViewOpen({
                type: 'OPEN',
                payload: {
                    viewType: props.openTo === 'edit' ? 'EDIT' : 'SHOW',
                    id,
                },
            });
        },
        [dispatchViewOpen, props.openTo],
    );
    const handleClose = useCallback(
        (e?: React.MouseEvent<HTMLButtonElement>) => {
            if (e) {
                e.preventDefault();
                e.stopPropagation();
            }
            dispatchViewOpen({ type: 'CLOSE' });
        },
        [dispatchViewOpen],
    );
    const handleSwitchToShow = useCallback(() => {
        dispatchViewOpen({
            type: 'SWITCH_TO',
            payload: {
                viewType: 'SHOW',
            },
        });
    }, [dispatchViewOpen]);
    const handleSwitchToEdit = useCallback(() => {
        dispatchViewOpen({
            type: 'SWITCH_TO',
            payload: {
                viewType: 'EDIT',
            },
        });
    }, [dispatchViewOpen]);
    const handleDeleteSuccess = useCallback(() => {
        if (refresh) {
            refresh();
        }
        dispatchViewOpen({ type: 'CLOSE_WITH_SAVE' });
    }, [refresh, dispatchViewOpen]);
    const handleOnSaveEdit = useCallback(
        id => {
            dispatchViewOpen({
                type: 'OPEN',
                payload: {
                    id,
                    viewType: 'SHOW',
                },
            });
        },
        [dispatchViewOpen],
    );
    const location = useMemo(() => {
        return { pathname: `/${reference}` };
    }, [reference]);
    const match = useMemo(() => {
        return {
            isExact: true,
            params: {
                id: selectedId,
                resourceBasePath: `/${reference}`,
            },
        };
    }, [reference, selectedId]);
    const DialogView = (
        <Dialog
            maxWidth={false}
            fullWidth={true}
            TransitionProps={DialogTransitionProps}
            open={viewOpen.type === 'OPEN'}
            onClose={handleClose}
        >
            <div>
                {viewOpen.type === 'OPEN' &&
                    (viewOpen.viewType === 'EDIT' ? (
                        <GenericEdit
                            noRedirectOnIdChange={true}
                            isPopover={true}
                            formId={`${reference}-Edit-${formId}`}
                            actions={
                                <EditActions
                                    accessLevel={getAccessLevelForEntity(viewConfig, props.reference)}
                                    resource={reference}
                                    viewName={props.editViewName as string}
                                    basePath={`/${reference}`}
                                    hasDefaultShow={false} // use our provided show button
                                    hasDefaultDelete={false} // use provided delete
                                >
                                    <Button color="primary" onClick={handleSwitchToShow}>
                                        View <ImageEye />
                                    </Button>
                                    <InlineDeleteButton
                                        data-minAccessLevel={5}
                                        id={selectedId}
                                        resource={props.reference}
                                        renderIcon={({ handleClick, classes }) => (
                                            <Button color="secondary" onClick={handleClick} className={classes.del}>
                                                Delete <DeleteIcon />
                                            </Button>
                                        )}
                                        onDeleteSuccess={handleDeleteSuccess}
                                    />
                                </EditActions>
                            }
                            viewName={
                                props.editViewName ||
                                getCustomViewName('EDIT')(props.reference, viewConfig, props.config)
                            }
                            redirect={false}
                            onSaveCb={handleOnSaveEdit}
                            resource={reference}
                            location={location}
                            match={match}
                            toolbar={
                                <Toolbar>
                                    <SaveButton />
                                    <CloseButton handleClose={handleClose} />
                                </Toolbar>
                            }
                        />
                    ) : (
                        <GenericShow
                            isPopover={true}
                            actions={
                                <EntityInspectShowActions
                                    handleEditOpen={handleSwitchToEdit}
                                    editViewName={props.editViewName}
                                    resource={props.reference}
                                    viewName={props.showViewName}
                                    basePath={`/${props.reference}`}
                                    showActionChildren={props.showActionChildren}
                                />
                            }
                            resource={reference}
                            location={location}
                            match={match}
                            formId={`${reference}-show-${formId}`}
                            viewName={
                                props.showViewName ||
                                getCustomViewName('SHOW')(props.reference, viewConfig, props.config)
                            }
                            toolbar={
                                <Toolbar>
                                    <CloseButton handleClose={handleClose} />
                                </Toolbar>
                            }
                        />
                    ))}
            </div>
        </Dialog>
    );
    return (
        <React.Fragment>
            {renderComponent({
                formId,
                reference,
                keyForReload: viewOpen.keyForReload,
                onRowSelect,
                selectedId,
                selectId,
            })}
            {DialogView}
        </React.Fragment>
    );
};
export default EntityInspect;
